import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import * as yup from 'yup';
import { useFieldArray } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import {
  useExtractAgriInputScheduleItemsMutation,
  useLazyGetTableCSVPresignedURLByTableTypeQuery,
} from '../../../services/mVPGraphQlService';
import { stageLabel } from '../utils/scheduleStage';
import {
  buildLineSchema,
  StageRanges,
} from '../utils/scheduleLineSchema';
import { useStageOptions } from '../hooks/useStageOptions';
import { useScheduleLineAutofill } from '../hooks/useScheduleLineAutofill';
import LockedField from './lockedField';
import { useFormWithSchema } from '../../../utils/functions/yupFormTypescript';
import {
  AutoCompleteController,
  InputController,
} from '../../../components';
import {
  CascadeField,
  useCropSeasonOptions,
} from '../hooks/useCropSeasonOptions';
import {
  formatProductLabel,
  useProductOptions,
} from '../hooks/useProductOptions';
import arrow from '../../../assets/icons/line_arrow-close-left.svg';

const TEAL = 'rgba(0,105,112,1)';

const COUNTRY_OPTIONS = [
  { value: 'INDIA', label: 'India' },
  { value: 'THAILAND', label: 'Thailand' },
];

const MANDATORY_OPTIONS = [
  { value: 'YES', label: 'YES' },
  { value: 'NO', label: 'NO' },
];

export type TScheduleLine = {
  product: string;
  stage: string;
  dasFrom: string;
  dasTo: string;
  mandatory: string;
};

export type TImportScheduleForm = {
  country: string;
  region: string;
  season: string;
  crop: string;
  hybridProductionCode: string;
  remarks?: string;
  lines: TScheduleLine[];
};

const emptyLine: TScheduleLine = {
  product: '',
  stage: '',
  dasFrom: '',
  dasTo: '',
  mandatory: '',
};

const defaultValues: TImportScheduleForm = {
  country: '',
  region: '',
  season: '',
  crop: '',
  hybridProductionCode: '',
  remarks: '',
  lines: [],
};

const importScheduleSchema = yup.object({
  country: yup.string(),
  region: yup.string().required('Region is required'),
  season: yup.string().required('Season is required'),
  crop: yup.string().required('Crop is required'),
  hybridProductionCode: yup
    .string()
    .required('Hybrid Production Code is required'),
  remarks: yup.string(),
  lines: yup
    .array()
    .of(buildLineSchema(true))
    .min(1, 'At least one schedule line is required'),
});

type ImportScheduleDrawerProps = {
  open: boolean;
  onClose: () => void;
  onSave?: (
    data: TImportScheduleForm
  ) => Promise<boolean | void> | boolean | void;
  onPublish?: (
    data: TImportScheduleForm
  ) => Promise<boolean | void> | boolean | void;
  onDelete?: () => void;
  saving?: boolean;
};

export default function ImportScheduleDrawer({
  open,
  onClose,
  onSave,
  onPublish,
  onDelete,
  saving = false,
}: ImportScheduleDrawerProps) {
  const countryName = useSelector(
    (state: RootState) => state.country.countryName
  );
  const wsId = useSelector(
    (state: RootState) => state.country.wsId
  );
  const defaultCountry =
    COUNTRY_OPTIONS.find(
      (opt) =>
        opt.value.toLowerCase() ===
        (countryName || '').toLowerCase()
    )?.value || '';

  const [getCSVUrl, { isFetching: isTemplateFetching }] =
    useLazyGetTableCSVPresignedURLByTableTypeQuery();
  const [getUploadUrl] =
    useLazyGetTableCSVPresignedURLByTableTypeQuery();
  const [extractItems] =
    useExtractAgriInputScheduleItemsMutation();

  const handleDownloadTemplate = async () => {
    try {
      const response = (await getCSVUrl({
        tableType: 'TT_AGRI_INPUT_SCHEDULE_ITEMS',
        csvFileType: 'TEMPLATE_FILE',
        wsId,
      } as any).unwrap()) as any;

      const downloadUrl =
        response?.data
          ?.GetTableCSVPresignedURLByTableType
          ?.templateUrl;

      if (!downloadUrl) {
        throw new Error('Template URL not found');
      }
      window.open(downloadUrl, '_blank');
    } catch (err) {
      console.error('Download template failed', err);
    }
  };
  const validationContext = useRef<{ stageRanges: StageRanges }>({
    stageRanges: {},
  }).current;
  const {
    formState: { errors, isValid },
    register,
    control,
    reset,
    watch,
    getValues,
    setValue,
    trigger,
  } = useFormWithSchema(importScheduleSchema, {
    mode: 'onChange',
    defaultValues,
    context: validationContext,
  });

  const [watchRegion, watchSeason, watchCrop, watchHybrid] =
    watch([
      'region',
      'season',
      'crop',
      'hybridProductionCode',
    ]);
  const canSave = Boolean(
    defaultCountry?.trim() &&
      watchSeason?.trim() &&
      watchCrop?.trim() &&
      watchHybrid?.trim()
  );
  const canUpload = canSave && Boolean(watchRegion?.trim());

  const clearField = useCallback(
    (field: CascadeField) => {
      const opts = { shouldValidate: true };
      if (field === 'season') setValue('season', '', opts);
      else if (field === 'region') setValue('region', '', opts);
      else if (field === 'crop') setValue('crop', '', opts);
      else if (field === 'hybrid')
        setValue('hybridProductionCode', '', opts);
    },
    [setValue]
  );

  const { productOptions, uplByProductId: apiUplByProductId } =
    useProductOptions(open, {
      region: watchRegion,
      crop: watchCrop,
      hybrid: watchHybrid,
    });

  const {
    seasonOptions,
    regionOptions,
    cropOptions,
    hybridOptions,
  } = useCropSeasonOptions({
    enabled: open,
    season: watchSeason,
    region: watchRegion,
    crop: watchCrop,
    hybrid: watchHybrid,
    clear: clearField,
  });

  const { fields, prepend, remove, replace } =
    useFieldArray({
      control,
      name: 'lines',
    });

  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [extraProducts, setExtraProducts] = useState<
    { value: string; label: string; upl: boolean }[]
  >([]);
  const lineProductOptions = useMemo(() => {
    const known = new Set(productOptions.map((o) => o.value));
    return [
      ...productOptions,
      ...extraProducts
        .filter((o) => !known.has(o.value))
        .map(({ value, label }) => ({ value, label })),
    ];
  }, [productOptions, extraProducts]);
  const uplByProductId = useMemo(() => {
    const map: Record<string, boolean> = {};
    extraProducts.forEach((o) => {
      map[o.value] = o.upl;
    });
    return { ...map, ...apiUplByProductId };
  }, [extraProducts, apiUplByProductId]);

  const watchLines = watch('lines');
  const {
    stageOptions,
    stageRanges,
    requestStages,
    loading: stagesLoading,
  } = useStageOptions({
    enabled: open,
    crop: watchCrop,
    hybrid: watchHybrid,
    season: watchSeason,
  });
  validationContext.stageRanges = stageRanges;

  useEffect(() => {
    if (open && fields.length > 0) requestStages();
  }, [open, fields.length, requestStages]);

  useEffect(() => {
    if (open && fields.length > 0) trigger('lines');
  }, [open, stageRanges, fields.length, trigger]);
  const lineStageOptions = useMemo(() => {
    const known = new Set(stageOptions.map((o) => o.value));
    const extra = Array.from(
      new Set(
        (watchLines ?? [])
          .map((line) => line?.stage)
          .filter((v): v is string => !!v && !known.has(v))
      )
    ).map((value) => ({ value, label: value }));
    return [...stageOptions, ...extra];
  }, [stageOptions, watchLines]);

  useScheduleLineAutofill({
    lines: watchLines,
    rowIds: fields.map((field) => field.id),
    setValue,
    uplByProductId,
    stageRanges,
  });

  const rangeOf = (index: number) =>
    stageRanges[watchLines?.[index]?.stage ?? ''];

  const isUplLine = (index: number) =>
    uplByProductId[watchLines?.[index]?.product ?? ''] === true;
  const fileInputRef = useRef<HTMLInputElement | null>(
    null
  );

  useEffect(() => {
    if (open) {
      reset({ ...defaultValues, country: defaultCountry });
      setFile(null);
      setFileError('');
    }
  }, [open, reset, defaultCountry]);

  const handleClose = () => {
    reset({ ...defaultValues, country: defaultCountry });
    setFile(null);
    setFileError('');
    onClose();
  };

  

  const rejectFile = (message: string) => {
    setFile(null);
    replace([]);
    setFileError(message);
  };

  const uploadAndExtract = async (selectedFile: File) => {
    setUploading(true);
    try {
      const presigned = (await getUploadUrl({
        tableType: 'TT_AGRI_INPUT_SCHEDULE_ITEMS',
        count: 1,
        csvFileType: 'UPLOADED_FILE',
        wsId,
      } as any).unwrap()) as any;
      const { fileUrl, fileKey } =
        presigned?.data?.GetTableCSVPresignedURLByTableType
          ?.fileUploads?.[0] || {};
      if (!fileUrl || !fileKey) {
        rejectFile('Please try again.');
        return;
      }

      const upload = await fetch(fileUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'text/csv' },
        body: selectedFile,
      });
      if (!upload.ok) {
        rejectFile('Please try again.');
        return;
      }

      const extracted = await extractItems({
        wsId,
        fileKey,
        season: watchSeason,
        region: watchRegion,
        cropName: watchCrop,
        hybridProductionCode: watchHybrid,
      }).unwrap();
      const result =
        extracted?.data?.ExtractAgriInputScheduleItems;
      const status = result?.responseStatus;
      const failedRows = result?.errorCount ?? 0;

      if (status?.status !== 'SUCCESS') {
        rejectFile(
          status?.errorMessages?.[0] ||
            status?.reasons?.[0] ||
            'Please try again.'
        );
        return;
      }
      if (failedRows > 0) {
        const firstError = result?.errors?.[0];
        rejectFile(
          `${failedRows} ${
            failedRows === 1 ? 'row has' : 'rows have'
          } errors${
            firstError
              ? `. Row ${firstError.rowNumber}: ${firstError.message}`
              : '.'
          }`
        );
        return;
      }

      const items = (result?.items ?? []).filter(
        (item) => !item.deleted
      );
      setExtraProducts(
        items
          .filter((item) => item.productId != null)
          .map((item) => ({
            value: String(item.productId),
            label:
              formatProductLabel(item.product) ||
              String(item.productId),
            upl: item.product?.uplProduct === true,
          }))
      );
      replace(
        items.map((item) => ({
          product:
            item.productId != null ? String(item.productId) : '',
          stage: stageLabel(item.stage) || item.stageName || '',
          dasFrom: item.dasFrom != null ? String(item.dasFrom) : '',
          dasTo: item.dasTo != null ? String(item.dasTo) : '',
          mandatory: item.mandatory ? 'YES' : 'NO',
        }))
      );
    } catch (err) {
      console.error('Schedule file upload failed', err);
      rejectFile('Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (selectedFile: File) => {
    if (!canUpload || uploading) return;
    if (
      selectedFile.type !== 'text/csv' &&
      !selectedFile.name.toLowerCase().endsWith('.csv')
    ) {
      setFileError('Only CSV files are allowed.');
      return;
    }
    if (selectedFile.size > 500 * 1024) {
      setFileError('File size must be less than 500KB.');
      return;
    }
    setFileError('');
    setFile(selectedFile);
    uploadAndExtract(selectedFile);
  };

  const handleDrop = (
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (event.dataTransfer.files.length > 0) {
      handleFileSelect(event.dataTransfer.files[0]);
    }
  };

  const handleBrowse = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.files && event.target.files[0]) {
      handleFileSelect(event.target.files[0]);
    }
  };

  const removeFile = () => {
    setFile(null);
    replace([]);
  };


  const handleSave = async () => {
    if (!canSave || stagesLoading) return;
    if (fields.length > 0 && !(await trigger('lines'))) return;
    const result = await onSave?.({
      ...(getValues() as TImportScheduleForm),
      country: defaultCountry,
    });
    if (result !== false) handleClose();
  };

  const canSubmit = isValid && fields.length > 0 && !uploading;

  const [publishConfirmOpen, setPublishConfirmOpen] =
    useState(false);

  const requestPublish = () => {
    if (!canSubmit) return;
    setPublishConfirmOpen(true);
  };

  const confirmPublish = async () => {
    setPublishConfirmOpen(false);
    const result = await onPublish?.({
      ...(getValues() as TImportScheduleForm),
      country: defaultCountry,
    });
    if (result !== false) handleClose();
  };

  const gridFieldSx = {
    '& .form-control_autocomplete.h-28': {
      height: 'auto',
    },
    '& .form-field-label': { marginTop: '6px' },
    '& .input-field input': { marginBottom: 0 },
    '& .form-field-error': {
      margin: 0,
      minHeight: '16px',
      lineHeight: '16px',
      fontSize: '11px',
    },
  };

  
  const lineRowSx = {
    '& .form-control_autocomplete.h-28': {
      height: 'auto',
    },
    '& .form-field': {
      marginBottom: 0,
    },
    '& .form-field-error': {
      display: 'none',
    },
    '& .form-field-label': {
      marginTop: 0,
      marginBottom: '4px',
      fontSize: '12px',
      lineHeight: '16px',
      color: '#667085',
    },
    '& .input-field input': {
      boxSizing: 'border-box',
      margin: '0 !important',
      height: '36px !important',
      fontSize: '13px',
      borderRadius: '4px',
      border: '0.5px solid #d0d5dd',
      backgroundColor: '#fff',
    },
    '& .custom-autocomplete .MuiAutocomplete-inputRoot': {
      boxSizing: 'border-box',
      height: '36px !important',
      minHeight: '36px !important',
      padding: '0 8px !important',
    },
    '& .custom-autocomplete .MuiInputBase-root': {
      boxSizing: 'border-box',
      height: '36px !important',
      minHeight: '36px !important',
      fontSize: '13px',
      borderRadius: '4px',
      backgroundColor: '#fff',
    },
    '& .custom-autocomplete input': {
      fontSize: '13px',
      padding: '0 !important',
    },
    '& .custom-autocomplete fieldset': {
      borderWidth: '0.5px',
    },
    '& .input-field input:disabled': {
      backgroundColor: '#eceef1',
      color: '#98a2b3',
      cursor: 'not-allowed',
    },
    '& .MuiInputBase-root.Mui-disabled': {
      backgroundColor: '#eceef1',
    },
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: { width: '50vw', backgroundColor: '#fff' },
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: '1px solid #E0E0E0',
          boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
        }}>
        <Typography
          fontSize={17}
          fontWeight={600}
          sx={{ lineHeight: 1 }}>
          Add Schedule
        </Typography>
        <Box
          onClick={handleClose}
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}>
          <img
            src={arrow}
            alt="close"
            style={{ width: 20, height: 20 }}
          />
        </Box>
      </Box>

      <Box
        component="form"
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          pt: 2,
          pb: 10,
        }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              md: '1fr 1fr',
            },
            columnGap: 2,
            rowGap: 0,
            ...gridFieldSx,
          }}>
          <AutoCompleteController
            control={control}
            name="country"
            label="Country"
            placeholder="Select Country"
            options={COUNTRY_OPTIONS}
            required
            error={errors?.country?.message}
            disableClearable
            locked
          />
          <AutoCompleteController
            control={control}
            name="region"
            label="Region"
            placeholder="Select Region"
            options={regionOptions}
            required
            error={errors?.region?.message}
            disableClearable
          />
          <AutoCompleteController
            control={control}
            name="season"
            label="Season"
            placeholder="Select Season"
            options={seasonOptions}
            required
            error={errors?.season?.message}
            disableClearable
          />
          <AutoCompleteController
            control={control}
            name="crop"
            label="Crop"
            placeholder="Select Crop"
            options={cropOptions}
            required
            error={errors?.crop?.message}
            disableClearable
          />
          <AutoCompleteController
            control={control}
            name="hybridProductionCode"
            label="Hybrid Production Code"
            placeholder="Select Code"
            options={hybridOptions}
            required
            error={errors?.hybridProductionCode?.message}
            disableClearable
          />
          <Box sx={{ gridColumn: { md: '1 / -1' } }}>
            <InputController
              label="Remarks"
              placeholder="Enter remarks"
              register={register('remarks')}
              error={errors?.remarks?.message}
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mt: 1,
            mb: 1,
          }}>
          <Typography
            variant="body2"
            fontWeight={600}
            color="#444">
            Schedule Line Items
            {fields.length > 0 ? ` (${fields.length})` : ''}
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
            }}>
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={handleDownloadTemplate}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: 14,
                color: TEAL,
                fontWeight: 500,
              }}>
              <FileDownloadOutlinedIcon
                sx={{ fontSize: 18 }}
              />
              {isTemplateFetching
                ? 'Downloading...'
                : 'Download Template'}
            </Link>

            <Box
              sx={{
                width: '1px',
                height: 16,
                backgroundColor: '#d0d5dd',
              }}
            />

            <Link
              component="button"
              type="button"
              underline="none"
              disabled={!canSave}
              onClick={() => {
                if (canSave) {
                  requestStages();
                  prepend(emptyLine);
                }
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.25,
                fontSize: 14,
                color: canSave
                  ? TEAL
                  : 'rgba(0,105,112,0.4)',
                fontWeight: 500,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}>
              <AddIcon sx={{ fontSize: 18 }} />
              Add
            </Link>
          </Box>
        </Box>

        {!file ? (
          <Box
            onDrop={canUpload ? handleDrop : undefined}
            onDragOver={(e) => e.preventDefault()}
            sx={{
              borderRadius: 2,
              py: 4,
              px: 3,
              textAlign: 'center',
              backgroundColor: '#f5f5f5',
              cursor: canUpload ? 'pointer' : 'not-allowed',
              position: 'relative',
              opacity: canUpload ? 1 : 0.6,
            }}>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              disabled={!canUpload}
              onChange={handleBrowse}
              style={{
                opacity: 0,
                position: 'absolute',
                inset: 0,
                cursor: canUpload
                  ? 'pointer'
                  : 'not-allowed',
                pointerEvents: canUpload ? 'auto' : 'none',
              }}
            />
            <CloudUploadOutlinedIcon
              sx={{ fontSize: 32, color: '#777', mb: 0.5 }}
            />
            <Typography
              variant="body2"
              sx={{ color: '#666', mb: 0.5 }}>
              Drag & drop files or{' '}
              <Link
                component="button"
                type="button"
                underline="none"
                disabled={!canUpload}
                sx={{
                  color: TEAL,
                  fontWeight: 500,
                  cursor: canUpload
                    ? 'pointer'
                    : 'not-allowed',
                }}
                onClick={() => {
                  if (canUpload) fileInputRef.current?.click();
                }}>
                browse
              </Link>
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: '#999' }}>
              Supported formats: CSV (max. size 500KB)
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: `1px solid ${
                uploading ? '#d0d5dd' : '#12B76A'
              }`,
              backgroundColor: uploading ? '#f9fafb' : '#F6FEF9',
              borderRadius: 2,
              px: 2,
              py: 1.25,
            }}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}>
              {uploading ? (
                <CircularProgress size={18} sx={{ color: TEAL }} />
              ) : (
                <CheckCircleIcon
                  sx={{ color: '#12B76A', fontSize: 20 }}
                />
              )}
              <Typography
                fontSize={14}
                color={uploading ? '#475467' : '#027A48'}>
                {file.name}
              </Typography>
            </Box>
            <IconButton
              size="small"
              disabled={uploading}
              onClick={removeFile}>
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Box>
        )}

        {fileError && (
          <Box
            role="alert"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mt: 1.5,
              px: 1.5,
              py: 1.25,
              border: '1px solid #F04438',
              borderRadius: '4px',
              backgroundColor: '#FFF5F5',
            }}>
            <ErrorIcon
              sx={{ color: '#D92D20', fontSize: 18 }}
            />
            <Typography fontSize={13} color="#D92D20">
              File upload failed. {fileError}
            </Typography>
          </Box>
        )}

        {fields.length > 0 && (
          <Box sx={{ mt: 1.5 }}>
            {fields.map((lineField, index) => (
              <Box
                key={lineField.id}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  width: 688,
                  maxWidth: '100%',
                  minHeight: 73,
                  boxSizing: 'border-box',
                  border: '1px solid #EFEFEF',
                  backgroundColor: '#F2F2F2',
                  borderRadius: '4px',
                  p: '8px',
                  mb: 1.5,
                  ...lineRowSx,
                }}>
                
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <AutoCompleteController
                    control={control}
                    name={`lines.${index}.product`}
                    label="Product"
                    placeholder="Select product"
                    options={lineProductOptions}
                    disableClearable
                    height={36}
                    error={
                      errors?.lines?.[index]?.product
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 152, flexShrink: 0 }}>
                  <AutoCompleteController
                    control={control}
                    name={`lines.${index}.stage`}
                    label="Stage"
                    placeholder="Select stage"
                    options={lineStageOptions}
                    disableClearable
                    height={36}
                    error={
                      errors?.lines?.[index]?.stage
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  <InputController
                    label="DAS From"
                    placeholder={
                      rangeOf(index)?.dasFrom != null
                        ? `≥ ${rangeOf(index)?.dasFrom}`
                        : '0'
                    }
                    title={
                      errors?.lines?.[index]?.dasFrom?.message
                    }
                    register={register(
                      `lines.${index}.dasFrom`
                    )}
                    error={
                      errors?.lines?.[index]?.dasFrom
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  <InputController
                    label="DAS To"
                    placeholder={
                      rangeOf(index)?.dasTo != null
                        ? `≤ ${rangeOf(index)?.dasTo}`
                        : '0'
                    }
                    title={
                      errors?.lines?.[index]?.dasTo?.message
                    }
                    register={register(
                      `lines.${index}.dasTo`
                    )}
                    error={
                      errors?.lines?.[index]?.dasTo
                        ?.message
                    }
                  />
                </Box>
                <Box sx={{ width: 80, flexShrink: 0 }}>
                  {isUplLine(index) ? (
                    <LockedField label="Mandatory" value="YES" />
                  ) : (
                    <AutoCompleteController
                      control={control}
                      name={`lines.${index}.mandatory`}
                      label="Mandatory"
                      placeholder="Select"
                      options={MANDATORY_OPTIONS}
                      disableClearable
                      height={36}
                      error={
                        errors?.lines?.[index]?.mandatory
                          ?.message
                      }
                    />
                  )}
                </Box>
                <IconButton
                  size="small"
                  onClick={() => remove(index)}
                  sx={{ mt: 3, flexShrink: 0 }}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          position: 'sticky',
          bottom: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          px: 3,
          py: 2,
          borderTop: '1px solid #E0E0E0',
          backgroundColor: '#fff',
        }}>
        <Button
          variant="outlined"
          disabled={!canSave}
          onClick={() => {  
            removeFile();
            onDelete?.();
          }}
          sx={{
            textTransform: 'none',
            color: TEAL,
            borderColor: TEAL,
            fontWeight: 500,
            '&.Mui-disabled': {
              color: 'rgba(0,105,112,0.4)',
              borderColor: 'rgba(0,105,112,0.3)',
            },
          }}>
          Delete
        </Button>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}>
          <Button
            variant="outlined"
            disabled={!canSave || saving || stagesLoading}
            onClick={handleSave}
            sx={{
              textTransform: 'none',
              color: TEAL,
              borderColor: TEAL,
              fontWeight: 500,
              '&.Mui-disabled': {
                color: 'rgba(0,105,112,0.4)',
                borderColor: 'rgba(0,105,112,0.3)',
              },
            }}>
            Save
          </Button>
          <Button
            variant="contained"
            disabled={!canSubmit || saving}
            onClick={requestPublish}
            sx={{
              textTransform: 'none',
              backgroundColor: TEAL,
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
              '&.Mui-disabled': {
                backgroundColor: 'rgba(0,105,112,0.4)',
                color: '#fff',
              },
            }}>
            Publish Schedule
          </Button>
        </Box>
      </Box>

      <Dialog
        open={publishConfirmOpen}
        onClose={() => setPublishConfirmOpen(false)}
        fullWidth
        maxWidth="xs">
        <DialogTitle
          sx={{
            fontSize: 15,
            fontWeight: 600,
            color: '#4D4D4D',
          }}>
          Publish Schedule
          <IconButton
            aria-label="close"
            onClick={() => setPublishConfirmOpen(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: '#4D4D4D',
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography fontSize={14} color="##292929">
            Are you sure you want to publish this schedule?
            Once published, it will be available for
            application.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setPublishConfirmOpen(false)}
            sx={{
              textTransform: 'none',
              color: TEAL,
              borderColor: TEAL,
              fontWeight: 500,
              '&:hover': {
                borderColor: 'rgba(0,105,112,0.8)',
                backgroundColor: 'rgba(0,105,112,0.05)',
              },
            }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={confirmPublish}
            sx={{
              textTransform: 'none',
              backgroundColor: TEAL,
              color: '#fff',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(0,105,112,0.9)',
              },
            }}>
            Publish
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
}
